import reducer, {
  entityById,
  findCollisions,
  INITIAL_STATE,
  Player,
} from "./reducer";
import Level from "./Level";
import KeyboardProvider from "./keyboard/KeyboardProvider";
import useKeyboard from "./keyboard/useKeyboard";
import GameOver from "./GameOver";
import { Canvas } from "./react-graphics";
import styled from "styled-components";
import useReducer from "./useReducer";
import {
  ENEMY_PROJECTILE_DAMAGE,
  ENEMY_SPAWN_INTERVAL,
  GRID_LEFT,
  GRID_RIGHT,
  HEALTH_POWER_UP_AMOUNT,
  PLAYER_PROJECTILE_DAMAGE,
  POWER_UP_SPAWN_DISTANCE,
  SCORE_PER_ENEMY,
} from "./constants";
import cuid from "cuid";

type Props = {
  canvasWidth: number;
  canvasHeight: number;
};

const Root = styled.div`
  background-color: #ffcc99;
`;

const Score = styled.div`
  position: fixed;
  top: 10px;
  left: 10px;
  font-family: monospace;
  font-size: 16px;
`;

const Game = ({ canvasWidth, canvasHeight }: Props) => {
  const [state, dispatch] = useReducer(
    reducer,
    INITIAL_STATE,
    (state, action) => {
      switch (action.type) {
        case "TICK": {
          // Collisions
          {
            const collisions = findCollisions(state);
            collisions.forEach((collision) => {
              const b = entityById(collision.b, state.entities);
              if (!b) {
                return;
              }
              const a = entityById(collision.a, state.entities);
              if (!a) {
                return;
              }
              if (a.type === "health-power-up" && b.type === "player") {
                dispatch({
                  type: "REPLENISH_HEALTH",
                  amount: HEALTH_POWER_UP_AMOUNT,
                  targetId: b.id,
                });
                dispatch({ type: "DIE", targetId: a.id });
              }
              if (a.type !== "projectile") {
                return;
              }
              const aOwner = entityById(a.ownerId, state.entities);
              if (!aOwner) {
                return;
              }
              if (b.type === "enemy" && aOwner.type === "player") {
                dispatch({
                  type: "REMOVE_PROJECTILE",
                  projectileId: collision.a,
                });
                if (b.health > PLAYER_PROJECTILE_DAMAGE) {
                  dispatch({
                    type: "DO_DAMAGE",
                    targetId: b.id,
                    amount: PLAYER_PROJECTILE_DAMAGE,
                  });
                  return;
                }
                dispatch({ type: "DIE", targetId: b.id });
                dispatch({
                  id: cuid(),
                  type: "SPAWN_WRECK",
                  position: b.position,
                  shotPosition: a.position,
                });
                dispatch({
                  type: "INCREASE_SCORE",
                  amount: SCORE_PER_ENEMY,
                });
              } else if (b.type === "player" && aOwner.type === "enemy") {
                dispatch({
                  type: "REMOVE_PROJECTILE",
                  projectileId: collision.a,
                });
                dispatch(
                  b.health > ENEMY_PROJECTILE_DAMAGE
                    ? {
                        type: "DO_DAMAGE",
                        targetId: b.id,
                        amount: ENEMY_PROJECTILE_DAMAGE,
                      }
                    : {
                        type: "DIE",
                        targetId: b.id,
                      }
                );
              }
            }, state);
          }

          // Spawn enemies
          if (state.time - state.enemySpawnedAt > ENEMY_SPAWN_INTERVAL) {
            dispatch({ type: "SPAWN_ENEMY" });
          }

          // Spawn health power-ups
          const player = state.entities.find(
            (entity) => entity.type === "player"
          ) as Player | undefined;
          if (player && player.position.y > state.lastPowerUpSpawnY - 1000) {
            dispatch({
              type: "SPAWN_POWER_UP",
              id: cuid(),
              position: {
                x: (GRID_RIGHT - GRID_LEFT) * (Math.random() - 0.5),
                y: state.lastPowerUpSpawnY + POWER_UP_SPAWN_DISTANCE,
              },
            });
          }

          return;
        }
        default: {
          return;
        }
      }
    }
  );
  const keyboard = useKeyboard();
  const player = state.entities.find((entity) => entity.type === "player") as
    | Player
    | undefined;

  return (
    <Root>
      <Score>Score: {state.score}</Score>
      <Canvas width={canvasWidth} height={canvasHeight}>
        <KeyboardProvider keyboard={keyboard}>
          <Level
            canvasWidth={canvasWidth}
            canvasHeight={canvasHeight}
            state={state}
            dispatch={dispatch}
          />
        </KeyboardProvider>
      </Canvas>
      {!player && (
        <GameOver
          onRestart={() => {
            dispatch({ type: "RESTART" });
          }}
        />
      )}
    </Root>
  );
};

export default Game;
